import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import ThemeProvider from "./theme/ThemeProvider";
import SnackbarProvider from "./ui/SnackbarProvider";
import SyncJobProvider from "./ui/SyncJobProvider";
import { PageBannerProvider } from "./ui/PageBannerProvider";
import UpdateNotifier from "./ui/UpdateNotifier";
import ChunkReloadBoundary, { installChunkErrorReload } from "./ui/ChunkReloadBoundary";
import BootstrapProvider from "./ui/BootstrapProvider";
import ProductOnboardingProvider from "./ui/ProductOnboardingProvider";
import FeaturesProvider from "./ui/FeaturesProvider";
import { PermissionsProvider } from "./ui/PermissionsProvider";
import RequireCapability from "./ui/RequireCapability";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import SharedBoardPage from "./pages/sharing/SharedBoardPage";
import AgencySitePage from "./pages/agents/website-assistant/public/AgencySitePage";
import DashboardLayout from "./pages/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import DashboardDetailPage from "./pages/DashboardDetailPage";
import ConnectorsPage from "./pages/ConnectorsPage";
import ConnectorDetailPage from "./pages/ConnectorDetailPage";
import DataRelationshipsPage from "./pages/DataRelationshipsPage";
import EntityDetailPage from "./pages/EntityDetailPage";
import CopilotPage from "./pages/copilot/CopilotPage";
import CopilotHomePage from "./pages/copilot/CopilotHomePage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import AISettingsPage from "./pages/settings/AISettingsPage";
import WorkspaceSettingsPage from "./pages/settings/WorkspaceSettingsPage";
import BrandingSettingsPage from "./pages/settings/BrandingSettingsPage";
import AccountSettingsPage from "./pages/settings/AccountSettingsPage";
import FeaturesSettingsPage from "./pages/settings/FeaturesSettingsPage";
import PredictiveModelsHubPage from "./pages/predictive-models/PredictiveModelsHubPage";
import PropensityListPage from "./pages/predictive-models/propensity/PropensityListPage";
import PropensityDetailPage from "./pages/predictive-models/propensity/PropensityDetailPage";
import SpacesPage from "./pages/SpacesPage";
import SpaceDetailPage from "./pages/SpaceDetailPage";
import PeoplePage from "./pages/people/PeoplePage";
import OnboardingWizard from "./pages/onboarding/OnboardingWizard";
import StartOnboardingPage from "./pages/onboarding/StartOnboardingPage";
import { ClaimGateProvider } from "./pages/onboarding/claim/ClaimGateContext";
import DataSyncPage from "./pages/DataSyncPage";
import DataSyncJobPage from "./pages/DataSyncJobPage";
import AgentsListPage from "./pages/agents/AgentsListPage";
import AgentPage from "./pages/agents/AgentPage";
import SalesDeskPage from "./pages/sales-desk/SalesDeskPage";
import { AgentOrPlaybook, LegacyAiAgentsRedirect } from "./pages/agents/agentRoutes";
import LeadToCashMonitorPage from "./pages/lead-to-cash/LeadToCashMonitorPage";
import DashboardRoute, { LegacyManualDashboardRedirect } from "./pages/DashboardRoute";
import DialingOpportunityPage from "./pages/dialing-opportunity/DialingOpportunityPage";
import ContactOptimizationPulsePage from "./pages/contact-optimization/PulsePage";
import SalesOpsTodayPage from "./pages/sales-operations/TodayPage";
import SalesOpsPlanPage from "./pages/sales-operations/PlanPage";
import SalesOpsQueuePage from "./pages/sales-operations/QueuePage";
import SalesOpsAgentsPage from "./pages/sales-operations/AgentsPage";
import ContactOptimizationTimingPage from "./pages/contact-optimization/TimingPage";
import ContactOptimizationPlanPage from "./pages/contact-optimization/PlanPage";
import ContactOptimizationModelPage from "./pages/contact-optimization/ModelPage";
import ContactOptimizationQueuePage from "./pages/contact-optimization/QueuePage";
import ContactOptimizationScorecardPage from "./pages/contact-optimization/ScorecardPage";
import BrokerMonitorPage from "./pages/broker-monitor/BrokerMonitorPage";
import DashboardsPulsePage from './pages/pulse/PulsePage';
import PlannerDashboardPage from './pages/manual-dashboard/planner/PlannerDashboardPage';
import SidebarAddressPage from './navigation/SidebarAddressPage';
import HomeRedirect from './navigation/HomeRedirect';

/**
 * Back-compat redirect for the pre-IA-change Predictive Models detail
 * URLs. `<Navigate>` doesn't interpolate path params on its own, so this
 * tiny component reads `:configId` and points at the new path.
 */
function RedirectPropensityDetail() {
  const { configId } = useParams<{ configId: string }>();
  return <Navigate to={`/predictive-models/propensity/${configId ?? ""}`} replace />;
}

function App() {
  // Defer PostHog init past FCP — posthog-js queues events until init() is called,
  // so no analytics are lost. The dynamic import keeps the SDK out of the critical chunk.
  React.useEffect(() => {
    import('./posthog');
  }, []);

  // Auto-reload a stale tab when a dynamic import fails post-deploy (chunk 404).
  React.useEffect(() => installChunkErrorReload(), []);

  return (
      <ChunkReloadBoundary>
      <BootstrapProvider>
      <ThemeProvider>
        <UpdateNotifier />
        <SnackbarProvider>
          <PermissionsProvider>
          <FeaturesProvider>
          <SyncJobProvider>
          <PageBannerProvider>
          <Router>
          <ProductOnboardingProvider>
          <ClaimGateProvider>
          <Routes>
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<OnboardingWizard />} />
            <Route path="/login-pwd" element={<LoginPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            {/* A shared link: no app around it, no workspace login. */}
            <Route path="/shared/:link/*" element={<SharedBoardPage />} />
            {/* A website visitor's view: the agency's page with its chat. No sign-in. */}
            <Route path="/w/:key" element={<AgencySitePage />} />
            {/* Always starts a fresh anonymous "explore without signing up" session. */}
            <Route path="/start" element={<StartOnboardingPage />} />
            {/* Post-OTP setup (path choice, sample data, etc.) - same wizard, URL synced in OnboardingWizard */}
            <Route path="/onboarding/*" element={<OnboardingWizard />} />
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<CopilotHomePage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="dashboard/:dashboardId" element={<DashboardRoute />} />
              {/* A planner board opened on one part of itself, so a sidebar can
                  give each its own row: Cost per submit, Campaign Performance.
                  Keyed, so moving between them starts each page fresh. */}
              <Route path="dashboard/:dashboardId/simulator" element={<PlannerDashboardPage key="simulator" view="simulator" />} />
              <Route path="dashboard/:dashboardId/campaigns" element={<PlannerDashboardPage key="campaigns" view="campaigns" />} />
              {/* Superseded by the shared route above; kept so existing links resolve. */}
              <Route path="manual-dashboard/:dashboardId" element={<LegacyManualDashboardRedirect />} />
              {/* The agents' earlier address; old links land on /agents. */}
              <Route path="ai-agents/*" element={<LegacyAiAgentsRedirect />} />
              <Route path="spaces" element={<SpacesPage />} />
              <Route path="spaces/:spaceId" element={<SpaceDetailPage />} />
              <Route path="saved-queries" element={<SpacesPage />} /> {/* Backwards compatibility */}
              <Route path="connectors" element={<ConnectorsPage />} />
              <Route path="connectors/:connectorId" element={<ConnectorDetailPage />} />
              <Route path="data-sync" element={<DataSyncPage />} />
              <Route path="data-sync/:jobId" element={<DataSyncJobPage />} />
              <Route path="data-model" element={<DataRelationshipsPage />} />
              <Route path="data-model/entity/:entityName" element={<EntityDetailPage />} />
              <Route path="knowledge" element={<KnowledgeBasePage />} />
              {/* Settings — split by audience and concern. */}
              <Route path="settings/ai" element={<RequireCapability capability="manage_workspace"><AISettingsPage /></RequireCapability>} />
              <Route path="settings/workspace" element={<RequireCapability capability="manage_workspace"><WorkspaceSettingsPage /></RequireCapability>} />
              <Route path="settings/features" element={<RequireCapability capability="manage_features"><FeaturesSettingsPage /></RequireCapability>} />
              {/* Predictive Models — top-level Intelligence section. Old
                  /settings/predictive-models/* routes redirect to the new
                  paths so bookmarks from before the IA change still resolve. */}
              <Route path="predictive-models" element={<RequireCapability capability="manage_predictive_models"><PredictiveModelsHubPage /></RequireCapability>} />
              <Route path="predictive-models/propensity" element={<RequireCapability capability="manage_predictive_models"><PropensityListPage /></RequireCapability>} />
              <Route path="predictive-models/propensity/:configId" element={<RequireCapability capability="manage_predictive_models"><PropensityDetailPage /></RequireCapability>} />
              <Route path="predictive-models/contact-optimization" element={<RequireCapability capability="manage_predictive_models"><ContactOptimizationModelPage /></RequireCapability>} />
              <Route path="settings/predictive-models" element={<Navigate to="/predictive-models/propensity" replace />} />
              <Route path="settings/predictive-models/:configId" element={<RedirectPropensityDetail />} />
              <Route path="settings/branding" element={<RequireCapability capability="manage_workspace"><BrandingSettingsPage /></RequireCapability>} />
              <Route path="settings/account" element={<AccountSettingsPage />} />
              {/* Backwards compatibility — redirect old flat routes to the new IA. */}
              <Route path="settings" element={<Navigate to="/settings/ai" replace />} />
              <Route path="settings/organization" element={<Navigate to="/settings/account" replace />} />
              <Route path="branding" element={<Navigate to="/settings/branding" replace />} />
              <Route path="profile" element={<Navigate to="/settings/account" replace />} />
              {/* People — Members and Roles as tabs under one heading, so an
                  admin answering "who can do what" stays on one page. */}
              <Route path="users" element={<RequireCapability capability="manage_members"><PeoplePage /></RequireCapability>} />
              <Route path="users/roles" element={<RequireCapability capability="manage_members"><PeoplePage /></RequireCapability>} />
              <Route path="users/shared" element={<RequireCapability capability="manage_workspace"><PeoplePage /></RequireCapability>} />
              <Route path="copilot" element={<CopilotHomePage />} />
              {/* The workspace's chosen home page (navigation/home.ts). */}
              <Route path="home" element={<HomeRedirect />} />
              <Route path="copilot/:threadId" element={<CopilotPage />} />
              {/* Agents: the workspace's agents (ROCCO, …) as tiles above its saved
                  analyses. A playbook opens at /agents/<its id>, a workspace agent
                  at /agents/<its kind>/<section> — see pages/agents/agentRoutes. */}
              {/* Every lead the sales agents have worked on, as one list. */}
              <Route path="sales-desk" element={<SalesDeskPage />} />
              <Route path="agents" element={<AgentsListPage />} />
              <Route path="agents/:playbookId" element={<AgentOrPlaybook />} />
              <Route path="agents/:agentKind/:section" element={<AgentPage />} />
              {/* One thing inside a section, on a page of its own: a quote. */}
              <Route path="agents/:agentKind/:section/:itemId" element={<AgentPage />} />
              <Route path="lead-to-cash" element={<LeadToCashMonitorPage />} />
              <Route path="monitoring/dialing-opportunity" element={<DialingOpportunityPage />} />
              {/* Sales Operations (ROCCO) -- gated by the sales_operations feature flag */}
              <Route path="sales-operations" element={<SalesOpsTodayPage />} />
              <Route path="sales-operations/plan" element={<SalesOpsPlanPage />} />
              <Route path="sales-operations/queue" element={<SalesOpsQueuePage />} />
              <Route path="sales-operations/agents" element={<SalesOpsAgentsPage />} />
              <Route path="monitoring/contact-optimization" element={<ContactOptimizationPulsePage />} />
              <Route path="monitoring/contact-optimization/timing" element={<ContactOptimizationTimingPage />} />
              <Route path="monitoring/contact-optimization/plan" element={<ContactOptimizationPlanPage />} />
              <Route path="monitoring/contact-optimization/queue" element={<ContactOptimizationQueuePage />} />
              <Route path="monitoring/contact-optimization/scorecard" element={<ContactOptimizationScorecardPage />} />
              {/* The Model tab moved to Predictive Models 2026-08-03 -- it's a
                  model artifact, not a live view of "today's contact rate",
                  so it fits that section better. Keep the old URL working. */}
              <Route path="monitoring/contact-optimization/model" element={<Navigate to="/predictive-models/contact-optimization" replace />} />
              <Route path="broker-monitor" element={<BrokerMonitorPage />} />
              {/* The dashboards section: four categories, each holding its
                  views. Sales opens on Pulse; the rest arrive as views move in. */}
              <Route path="dashboards/sales" element={<DashboardsPulsePage />} />
              {/* Anything else inside the app: a board at the readable address
                  its sidebar row gives it (/marketing/campaign-performance), or
                  Copilot when no row claims the address. */}
              <Route path="*" element={<SidebarAddressPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/copilot" replace />} />
          </Routes>
          </ClaimGateProvider>
          </ProductOnboardingProvider>
        </Router>
          </PageBannerProvider>
          </SyncJobProvider>
          </FeaturesProvider>
          </PermissionsProvider>
        </SnackbarProvider>
    </ThemeProvider>
    </BootstrapProvider>
    </ChunkReloadBoundary>
  );
}

export default App;
